import { Controller } from '@hotwired/stimulus'

// Renders SVG connector lines for the training map tech tree (vertical layout).
//
// The server renders nodes into rows (depth levels). This controller measures
// each node's position and draws vertical bezier-curve connectors from the
// bottom-centre of a prerequisite card to the top-centre of a dependent card.
//
// Targets:
//   canvas  – the <svg> element that receives the connector lines
//   node    – each training map node card
//
// Each node carries:
//   data-tech-tree-node-id              – the training map UUID
//   data-tech-tree-prerequisite-ids     – JSON array of prerequisite UUIDs
export default class extends Controller {
  static targets = ['canvas', 'node']

  connect () {
    this._draw()
    this._resizeObserver = new ResizeObserver(() => this._draw())
    this._resizeObserver.observe(this.element)
  }

  disconnect () {
    this._resizeObserver?.disconnect()
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  _draw () {
    if (!this.hasCanvasTarget) return

    const svg = this.canvasTarget
    const containerRect = svg.getBoundingClientRect()

    // Build id → node element map
    const nodeMap = {}
    this.nodeTargets.forEach((node) => {
      const id = node.dataset.techTreeNodeId
      if (id) nodeMap[id] = node
    })

    // Collect edges {from: prereqId, to: dependentId}
    const edges = []
    this.nodeTargets.forEach((node) => {
      const toId = node.dataset.techTreeNodeId
      let prereqIds = []
      try {
        prereqIds = JSON.parse(node.dataset.techTreePrerequisiteIds || '[]')
      } catch (_e) {
        prereqIds = []
      }
      prereqIds.forEach((fromId) => {
        if (nodeMap[fromId]) edges.push({from: fromId, to: toId})
      })
    })

    svg.setAttribute('width', containerRect.width)
    svg.setAttribute('height', containerRect.height)

    while (svg.firstChild) svg.removeChild(svg.firstChild)

    edges.forEach(({from, to}) => {
      const fromNode = nodeMap[from]
      const toNode = nodeMap[to]
      if (!fromNode || !toNode) return

      const fromRect = fromNode.getBoundingClientRect()
      const toRect = toNode.getBoundingClientRect()

      // Exit point: bottom-centre of the prerequisite node
      const x1 = fromRect.left + fromRect.width / 2 - containerRect.left
      const y1 = fromRect.bottom - containerRect.top

      // Entry point: top-centre of the dependent node
      const x2 = toRect.left + toRect.width / 2 - containerRect.left
      const y2 = toRect.top - containerRect.top

      // Control point offset — 40% of vertical span for a smooth S-curve
      const cp = Math.abs(y2 - y1) * 0.4

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      path.setAttribute(
        'd',
        `M ${x1} ${y1} C ${x1} ${y1 + cp}, ${x2} ${y2 - cp}, ${x2} ${y2}`
      )
      path.setAttribute('fill', 'none')
      path.setAttribute('stroke', '#94a3b8')
      path.setAttribute('stroke-width', '2')
      path.setAttribute('stroke-linecap', 'round')
      svg.appendChild(path)
    })
  }
}
